import React, { FunctionComponent, useEffect, useCallback, useState, useRef, Fragment } from "react";
import { createRoot, RootType } from "react-dom/client";
import { ConveySocket, ConveyApi, ILang, CNeuralNames, CNueralDialectMap, IConversationEntity } from "@utils";
import { connect } from "react-redux";
import { IRootState } from "@utils";
import { formatPhoneNumber, formatSentence, delay } from "@lib/utils/helpers";
import moment from "moment-timezone";
import Markdown from "react-markdown";
import { Spinner } from "@components/Spinner";
import { store } from "@utils/helpers";
import { ChatInput, ChatInputContainer, ChatButton } from "@components/chatStyles";
import { SwitchWrap, SwitchItem, ActionsContainer, FlexCenterRow, FormInput, SDKButton } from "@components/styles";
import Switch from "react-switch";
import { setConversationEntity } from "@actions";
import {
  KeywordsContainer,
  Keyword,
  KeywordTimestamp,
  AudioWrap,
  UploadWrap,
  TranscribeTimestamp,
  TranscribeWrapContainer,
  TranscribeContainer,
  TranscribeTextWrap,
  TranscribeText,
  CaptionFade,
  TranscribeTextHeader,
  TranscribeTextIdentity,
  TranscribeWrap,
  SentimentContainer,
  SentimentScore
} from "./transcribeStyles";
import { capitalize } from "@lib/utils/helpers";
import { announcePolite } from "@components/LiveRegion";
import { InterpretationView } from "./InterpretationView";
import { TranscriptStore, isEnglishLang, isStreamDebug } from "./stream";
import { createUserScrollTracker, isScrolledAboveBottom, scrollElementToBottom } from "./autoScroll";
import { createAudioProcessorNode } from "./audioProcessor";
// @ts-ignore
import Play from "@assets/icons/play.svg";
// @ts-ignore
import Pause from "@assets/icons/pause.svg";
// @ts-ignore
import Phone from "@assets/icons/hangup.svg";

interface ITranscribeChatProps {
  agencyId: string;
  correlationId: string;
  callerLanguage: string;
  talkback?: boolean;
  showUploader?: boolean;
  conversationId?: string;
  conversationAgencyId?: string;
  $summaryContainer?: string;
  $sentimentContainer?: string;
  $keywordsContainer?: string;
  neuralName: string;
  app: IRootState["app"];
  agency: IRootState["agency"];
  conversations: IRootState["conversations"];
  callback?: (eventData: Record<any, any>) => void;
  onKeywordCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSentimentScoreCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSummaryCallback?: (eventData: string, isNew: boolean) => void;
  onLiveAudioCallback?: (eventData: Record<any, any>, dualChannel: boolean, live: boolean) => void;
  onUIReadyCallback?: () => void;
  recipientAudioFile?: File;
  agentAudioFile?: File;
  dualChannel?: boolean; // should be referred when trying call simulator
  numberCapabilities?: string;
  interpretationView?: boolean;
  streaming?: boolean;
  setConversationEntity: typeof setConversationEntity;
}

// A live voice call can be listened to on demand. Deliberately looser than
// `hasCall` (which also gates call controls on a connected leg) because the
// stream exists as soon as the voice call is active.
const hasLiveVoiceStream = (data?: IConversationEntity) =>
  Boolean(data && data.channel === "voice" && data.status === "active");

const transcriptSourceLang = (t: any): string =>
  (t?.sourceLang || t?.language || t?.data?.languages?.from || t?.data?.language || "").toString();

const hasCall = (data: IConversationEntity) => {
  console.log({
    data,
    active: data.status === "active",
    channel: data.channel === "voice",
    connected: data.connection_state === "connected"
  });
  return data.status === "active" && data.channel === "voice" && data.connection_state === "connected";
};

// Deterministic transcript ordering. `displaySeq` is the primary key; ties are
// broken by `start` time then the stable `id` so the SAME set of events always
// renders in the SAME order regardless of async arrival order (prevents the
// grouping/speaker-header from shifting run-to-run).
const stableTranscriptCompare = (a: any, b: any): number =>
  (a.displaySeq || 0) - (b.displaySeq || 0) ||
  (a.start || 0) - (b.start || 0) ||
  String(a.id).localeCompare(String(b.id));

const XTranscribeChat: FunctionComponent = ({
  correlationId,
  callback,
  onKeywordCallback,
  onSummaryCallback,
  onSentimentScoreCallback,
  onLiveAudioCallback,
  onUIReadyCallback,
  showUploader,
  callerLanguage,
  app,
  conversationId,
  conversationAgencyId,
  conversations,
  recipientAudioFile,
  agentAudioFile,
  dualChannel, // should be referred when trying call simulator
  numberCapabilities,
  interpretationView,
  streaming,
  $summaryContainer,
  setConversationEntity
}: ITranscribeChatProps) => {
  const currentConversation = conversations[conversationId];
  console.log({ currentConversation });
  const { agencyId: appAgencyId } = app;
  const agencyId =
    currentConversation?.entity?.agency_id || conversationAgencyId || appAgencyId;
  const [transcripts, setTranscriptions] = useState([]);
  const [terpStatus, setTerpStatus] = useState("");
  const [, setFoundKeywords] = useState({});
  const [terpConnecting, setTerpConnecting] = useState(false);
  const [showCallControls, setShowCallControls] = useState(currentConversation?.entity ? hasCall(currentConversation?.entity) : false);
  const foundKeywordsRef = useRef([]);
  const allKeywordsRef = useRef({});
  const hasAudioRef = useRef(false);
  const autoScrollRef = useRef(true);
  const userScrollRef = useRef(createUserScrollTracker());
  const wsRef = useRef({});
  const transcriptsIdentitiesRef = useRef([]);
  const sentimentScoresRef = useRef([]);
  const audioArray = useRef<Float32Array | null>(null);
  const allTranscriptsRef = useRef(new Map<string, any>());
  const activeRecognizingRef = useRef(new Map<string, string>());
  const utteranceEndsRef = useRef([]);
  const legacyUtteranceGenRef = useRef(new Map<string, number>());
  const recentUtteranceFinalRef = useRef(new Map<string, string>());
  const stableTranscriptsRef = useRef(new Map<string, any>());
  const useStablePipelineRef = useRef(false);
  const interpretationViewRef = useRef(!!interpretationView);
  interpretationViewRef.current = !!interpretationView;
  // Single source of truth for the interpretation view: a deterministic stream
  // store fed by the socket handlers. Created lazily so it is available on the
  // first render (before effects run).
  const storeRef = useRef<TranscriptStore | null>(null);
  const getStore = (): TranscriptStore => {
    if (!storeRef.current) {
      storeRef.current = new TranscriptStore(conversationId);
      if (conversationId) storeRef.current.holdUntilHistory();
    }
    return storeRef.current;
  };
  if (interpretationView) getStore();
  const streamingRef = useRef(!!streaming);
  streamingRef.current = !!streaming;
  const aiModeEnabledRef = useRef(false);
  const audioNodeRef = useRef<AudioWorkletNode>(null);
  const audioContextRef = useRef<AudioContext>(null);
  // Live audio is only streamed to consumers that joined the call's dedicated
  // SocketIO room, so the surface has to remember what it subscribed to in order
  // to release it again on Stop or teardown.
  const liveAudioTargetRef = useRef<{ agencyId: string; correlationId: string } | null>(null);
  const wsConnectInterval = useRef<NodeJS.Timer | null>(null);
  const summaryRef = useRef<RootType>(null);
  const recipientLanguageRef = useRef("en");
  const dualChannelRef = useRef(false);
  const [, setRecipientLanguage] = useState("");
  const [pcmDataUrl, setPcmDataUrl] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#fff");
  const [sentimentScore, setSentimentScore] = useState("");
  const [showAudioPlay, setShowAudioPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pcmLoaded, setPcmLoaded] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [hasAudio, setHasAudio] = useState(false);
  const [, setAudioNodeObj] = useState<AudioWorkletNode>();
  const [agentSelected, setAgentSelected] = useState(false);
  const [callerSelected, setCallerSelected] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [autoSpeakEnabled, setAutoSpeakEnabled] = useState(false);
  const [showLiveTranscriptions, setShowLiveTranscriptions] = useState(false);
  const [lastRecognizedAgentTranscript, setLastRecognizedAgentTranscript] = useState('');
  const [search, setSearch] = useState("");
  const currentLang = JSON.parse(store().getItem("neuralSelection"));
  const [, setLang] = useState<ILang>(
    currentLang
      ? { outputLanguage: currentLang.outputLanguage, neuralName: currentLang.neuralName }
      : {
          outputLanguage: "en-US",
          neuralName: "en-US-JennyNeural"
        }
  );

  // Live audio is requested on demand, so the CTA has to be offered before any
  // audio has arrived. Availability therefore follows the call itself: a live
  // voice call can be listened to, as can a surface with no conversation to
  // inspect (the call simulator), while a conversation that already has a
  // recorded PCM attachment is replayed through the media player CTA instead.
  const liveAudioAvailable =
    !pcmDataUrl &&
    (hasAudio || !currentConversation?.entity || hasLiveVoiceStream(currentConversation?.entity));

  const createAutoSpeak = useCallback(async (transcript: string) => {
    if (autoSpeakEnabled) {
      try {
        let rtpHost = '';
        if (currentConversation?.entity?.metadata?.handlerInstance) {
          rtpHost = currentConversation?.entity?.metadata?.handlerInstance;
          if (!rtpHost.startsWith("https://") && !rtpHost.startsWith("http://")) {
            rtpHost = `https://${rtpHost}`;
          }
          setSending(true);
          await ConveyApi.createAutoSpeak({
            inputText: transcript,
            conversationId,
            rtpHost
          });
        }
      } catch(err) {
        console.log('createAutoSpeak error:', { err });
      } finally {
        setSending(false);
      }
    }
  }, [autoSpeakEnabled]);

  const getAutoSpeakStatus = async () => {
    try {
      const channel = currentConversation?.entity?.channel;
      const status = currentConversation?.entity?.status;
      const providerNumber = currentConversation?.entity?.provider_number;
      const metadata = currentConversation?.entity?.metadata;
      if (channel === 'voice' && status === 'active' && providerNumber?.capabilities?.includes('speech')) {
        let rtpHost = '';
        if (metadata?.handlerInstance) {
          rtpHost = metadata?.handlerInstance;
          if (!rtpHost.startsWith("https://") && !rtpHost.startsWith("http://")) {
            rtpHost = `https://${rtpHost}`;
          }
          setSending(true);
          const currentAutoSpeakStatus = await ConveyApi.getAutoSpeakStatus({
            conversationId,
            rtpHost
          });
          setAutoSpeakEnabled(currentAutoSpeakStatus ? currentAutoSpeakStatus.autospeak || false : false);
        } else {
          setAutoSpeakEnabled(false);
        }
      }
    } catch(err) {
      console.log('getAutoSpeakStatus error:', { err });
      setAutoSpeakEnabled(false);
    } finally {
      setSending(false);
    }

  };

  const enableAiInterpretation = async (entityOverride?: Record<any, any>) => {
    if (!interpretationViewRef.current || aiModeEnabledRef.current || !conversationId) return;
    const entity = entityOverride || currentConversation?.entity;
    const channel = entity?.channel;
    const status = entity?.status;
    const metadata = entity?.metadata;
    if (channel !== "voice" || status !== "active" || !metadata?.handlerInstance) return;
    let rtpHost = metadata.handlerInstance;
    if (!rtpHost.startsWith("https://") && !rtpHost.startsWith("http://")) {
      rtpHost = `https://${rtpHost}`;
    }
    try {
      const res = await ConveyApi.setAiMode({ conversationId, enabled: true, rtpHost });
      if (res && res.aiMode === true) {
        aiModeEnabledRef.current = true;
      }
    } catch (err) {
      console.log("enableAiInterpretation error:", { err });
    }
  };

  const getIdentityLabel = (transcript) => {
    if (!transcript) return;
    return transcript.direction === "outbound"
      ? "Agent" //app.agencyName
      : formatPhoneNumber(decodeURIComponent(transcript.identity));
  };

  const scrollToBottom = () => {
    if (!autoScrollRef.current) return;
    // InterpretationView owns stick-to-bottom (smooth for new cards).
    if (interpretationViewRef.current) return;
    const wrapperEle = document.getElementById(`transcription-container-${correlationId}`);
    if (!wrapperEle) return;
    requestAnimationFrame(() => {
      if (!autoScrollRef.current) return;
      scrollElementToBottom(wrapperEle);
    });
  };

  const setAutoScrollEnabled = (enabled: boolean) => {
    autoScrollRef.current = enabled;
    setAutoScroll(enabled);
  };

  const handleUserScrollIntent = () => {
    userScrollRef.current.mark();
  };

  const handleTranscribeContainerScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!autoScrollRef.current || !userScrollRef.current.isUserScrolling()) return;
    if (isScrolledAboveBottom(event.currentTarget)) {
      setAutoScrollEnabled(false);
    }
  };

  const handleAutoScrollToggle = (checked: boolean) => {
    const enabled = !!checked;
    setAutoScrollEnabled(enabled);
    if (!enabled) return;
    const wrapperEle = document.getElementById(`transcription-container-${correlationId}`);
    if (!wrapperEle) return;
    requestAnimationFrame(() => {
      scrollElementToBottom(wrapperEle);
    });
  };

  useEffect(() => {
    return () => userScrollRef.current.dispose();
  }, []);

  const findKeywords = (data: Record<any, any>): string[][] => {
    const found = [];
    const dataRoot = data?.event ? data.event : data;
    return dataRoot.channel?.search
      ?.flatMap((searchItem) => {
        if (searchItem.hits.length > 0) {
          return searchItem.hits.map((hit) => {
            if (hit.confidence >= 0.9) {
              if (!found.includes(searchItem.hits[0].snippet)) {
                found.push(searchItem.hits[0].snippet);
                return [searchItem.query, searchItem.hits[0].snippet];
              }
            }
          });
        } else {
          return null;
        }
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (CNueralDialectMap[callerLanguage]) {
      const nueralNameIndex = CNueralDialectMap[callerLanguage];
      const lang = CNeuralNames[nueralNameIndex];
      store().setItem(
        "neuralSelection",
        JSON.stringify({
          language: nueralNameIndex,
          outputLanguage: lang.outputLanguage,
          neuralName: lang.neuralName
        })
      );
      setLang(lang);
    }
  }, [callerLanguage]);

  const getTimestamp = (updatedAt) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formattedTime = (updatedAt ? moment(updatedAt) : moment()).tz(tz).format("h:mm:ss a");
      return formattedTime;
    } catch (e) {
      return updatedAt;
    }
  };

  useEffect(() => {
    (async () => {
      if (!conversationId) {
        return;
      }
      
      let attachments = [];

      try {
        attachments = await ConveyApi.getConversationAttachments({ conversationId, agencyId });
      } catch(err) {
        console.log('Error fetching conversation attachments:', err);
        attachments = [];
      }

      const pcmFile = (attachments || []).find((a) => a.uri && a.uri.includes(".pcm"));

      if (pcmFile) {
        setPcmDataUrl(pcmFile.uri);
      }
    })();

    (async () => {
      if (!conversationId) {
        onUIReadyCallback && onUIReadyCallback();
        return;
      }
      
      setShowLiveTranscriptions(false);
      let keywords = [];
      let messagesResponse = [];

      try {
        keywords = await ConveyApi.getKeywords(agencyId);
      } catch(err) {
        console.log('Error fetching keywords:', err);
        keywords = [];
      }
      if (!Array.isArray(keywords)) keywords = [];

      const keywordsObj = {};
      keywords.forEach((keyword) => {
        if (keyword?.term) keywordsObj[keyword.term] = keyword;
      });
      allKeywordsRef.current = keywordsObj;

      try {
        messagesResponse = await ConveyApi.getMessages({ conversationId, agencyId });
      } catch (err) {
        console.log('Error fetching messages:', err);
        messagesResponse = [];
      }
      if (!Array.isArray(messagesResponse)) messagesResponse = [];
      if (interpretationViewRef.current) {
        try {
          const historyRows = messagesResponse.filter(
            (e) => e.source === "transcribe" || e.source === "speech"
          );
          getStore().loadHistory(historyRows);
        } catch (err) {
          console.log('Error loading transcription history:', err);
          getStore().loadHistory([]);
        }
        setShowLiveTranscriptions(true);
        onUIReadyCallback && onUIReadyCallback();
        return;
      }

      const histMsgs = messagesResponse
        .filter((e) => e.source === "transcribe" || e.source === "speech");
      // History rebuild must not mix live sip:stable rows still sitting in the
      // map (those carry per-leg emitSeq and no flush clock).
      allTranscriptsRef.current.clear();
      stableTranscriptsRef.current.clear();
      // Insertion order into the map is rewritten by seq turn-anchor below.
      histMsgs.forEach((m) => {
          const isAgent = m.direction === "outbound";

          let highlightWords = [];
          let parsedData = null;
          const createdAt = m.created_at;
          if (m.metadata) {
            try {
              parsedData = typeof m.metadata === "string" ? JSON.parse(m.metadata) : m.metadata;
              if (parsedData.provider_data) {
                if (
                  ["deepgram", "azure"].includes(parsedData.provider_data?.provider) &&
                  parsedData.provider_data?.data
                ) {
                  highlightWords = findKeywords(parsedData.provider_data?.data);
                }
              }
            } catch (error) {
              console.log({ error });
            }
          }

          let formattedIdentity: string;
          const rawIdentity = parsedData?.identity
            ? decodeURIComponent(parsedData.identity)
            : "";
          if (
            rawIdentity === "Caller" ||
            rawIdentity === "Interpreter" ||
            rawIdentity === "Agent" ||
            /^AI Interpreter/i.test(rawIdentity)
          ) {
            formattedIdentity = rawIdentity;
          } else if (rawIdentity) {
            formattedIdentity = formatPhoneNumber(rawIdentity);
          } else {
            formattedIdentity = isAgent
              ? "Agent"
              : formatPhoneNumber(
                  m.recipient_label && m.recipient_label !== "" ? m.recipient_label : m.recipient_number
                );
          }

          if (m.direction === "inbound" && m.language !== recipientLanguageRef.current) {
            recipientLanguageRef.current = m.language;
            setRecipientLanguage(m.language);
          }

          if (!transcriptsIdentitiesRef.current.includes(formattedIdentity)) {
            transcriptsIdentitiesRef.current.push(formattedIdentity);
          }

          const translated = !!m.body_transl && !!m.language && m.language !== "en" && m.body !== m.body_transl;
          let body = translated ? m.body_transl || m.message_body : m.message_body;
          const originalBody = m.body;
          
          const rawLang = (m.language || "").toString().toLowerCase();
          const bodyPlain = (originalBody || body || "").toString();
          const latinLetters = (bodyPlain.match(/[A-Za-z]/g) || []).length;
          const nonLatinLetters = (bodyPlain.match(/[^\s\d\p{P}\p{S}A-Za-z\u00C0-\u024F]/gu) || []).length;
          const englishBodyNoXlate =
            !!rawLang &&
            !rawLang.startsWith("en") &&
            !translated &&
            latinLetters >= 12 &&
            nonLatinLetters / Math.max(1, bodyPlain.length) < 0.12;
          const effectiveLanguage = englishBodyNoXlate ? "en" : m.language;

          if (highlightWords && highlightWords.length > 0) {
            highlightWords.forEach((words) => {
              const { direction: keywordDirection, level } = keywordsObj[words[0]];
              const highlightKeyword =
                keywordDirection === null ||
                (isAgent && keywordDirection?.includes("agent")) ||
                (!isAgent && keywordDirection?.includes("caller"));
              if (
                highlightKeyword &&
                words[1] &&
                body.toLowerCase().includes(words[1]) &&
                Object.keys(allKeywordsRef.current).includes(words[1])
              ) {
                let highlightColor = "#ef5350";
                if (level === 1) {
                  highlightColor = "#ef5350";
                } else if (level === 2) {
                  highlightColor = "#ffb22b";
                } else if (level === 3) {
                  highlightColor = "#186dde";
                }
                const regEx = new RegExp(words[1], "gi");
                body = formatSentence(
                  body.replace(regEx, `<strong style="color: ${highlightColor}">` + words[1] + "</strong>")
                );
                const payload = {
                  term: words[0],
                  data: keywordsObj[words[0]],
                  id: m.message_id,
                  created_at: m.created_at,
                  body: originalBody
                };
                foundKeywordsRef.current.push(payload);
                onKeywordCallback && onKeywordCallback(payload, false);
              }
            });
          }
          setFoundKeywords(foundKeywordsRef.current);

          allTranscriptsRef.current.set(m.message_id, {
            id: m.message_id,
            identity: formattedIdentity,
            dialogue: body,
            created_at: parsedData?.createdAt || createdAt,
            seq: parsedData?.displaySeq || parsedData?.seq || 0,
            timelineMs: parsedData?.timelineMs || parsedData?.displaySeq || null,
            start: parsedData?.start,
            language: effectiveLanguage,
            sourceLang: effectiveLanguage || "",
            status: "recognized",
            direction: m.direction,
            line: [formattedIdentity, body].join(": "),
            spokenText: translated ? originalBody : undefined,
            data: translated
              ? {
                  languages: {
                    from: m.language,
                    to: "en"
                  },
                  translations: {
                    from: m.body,
                    to: m.body_transl
                  }
                }
              : effectiveLanguage
              ? { languages: { from: effectiveLanguage } }
              : {}
          });
        });

      // Call-history / remount ONLY (live path uses displaySeq and is untouched).
      //
      // Required pipeline (same for Agent / Interpreter / Caller):
      //  1) one shared conversation timeline per message
      //  2) correct speaker label (from persisted identity)
      //  3) sort ALL messages by that timeline
      //  4) InterpretationView merges only when the previous card is the same speaker
      const histTime = (entry: any): number => {
        const ms = new Date(entry?.created_at).getTime();
        return Number.isNaN(ms) ? 0 : ms;
      };
      // Shared timeline: conversation flush clock (displaySeq / timelineMs) when
      // RTP persisted it. Never use per-leg emitSeq (4, 7, 11…) as a global key —
      // those are independent counters per Agent/Terp socket and scramble order.
      // Also never treat audio created_at as a flush clock — every ISO date is
      // >1e12 ms and would false-positive hasSharedClock.
      const hasFlushClock = (entry: any): boolean => {
        const seq = Number(entry?.seq) || 0;
        const tl = entry?.timelineMs;
        return seq > 1e12 || (typeof tl === "number" && tl > 1e12);
      };
      const sharedTimeline = (entry: any): number => {
        if (hasFlushClock(entry)) {
          const seq = Number(entry?.seq) || 0;
          if (seq > 1e12) return seq;
          return Number(entry.timelineMs);
        }
        // Without flush metadata, park after flush-clock rows when mixed.
        return histTime(entry);
      };

      const histAll = Array.from(allTranscriptsRef.current.values());
      const hasSharedClock = histAll.some((e) => hasFlushClock(e));
      const histOrdered = [...histAll].sort((a, b) => {
        if (hasSharedClock) {
          const aFlush = hasFlushClock(a) ? 0 : 1;
          const bFlush = hasFlushClock(b) ? 0 : 1;
          if (aFlush !== bFlush) return aFlush - bFlush;
        }
        return (
          sharedTimeline(a) - sharedTimeline(b) ||
          histTime(a) - histTime(b) ||
          String(a.id).localeCompare(String(b.id))
        );
      });

      histOrdered.forEach((entry, idx) => {
        allTranscriptsRef.current.set(entry.id, entry);
        stableTranscriptsRef.current.set(entry.id, {
          ...entry,
          displaySeq: idx + 1,
          revision: 1,
          finalizedDialogue: entry.dialogue,
          activeWord: "",
          spokenText: entry.spokenText ?? entry.data?.translations?.from,
          // Call-log / replay only — live rows never get this flag.
          historical: true
        });
      });

      // Historical feed follows turn-anchored displaySeq; live path unchanged.
      setTranscriptions(
        Array.from(stableTranscriptsRef.current.values()).sort(
          (a, b) => a.displaySeq - b.displaySeq
        )
      );
      setShowLiveTranscriptions(true);
      onUIReadyCallback && onUIReadyCallback();
      setTimeout(() => {
        scrollToBottom();
      }, 500);
    })().catch((err) => {
      console.log('Error initializing transcribe history:', err);
      setShowLiveTranscriptions(true);
      onUIReadyCallback && onUIReadyCallback();
    });

    const socketInstance = ConveySocket.init(ConveyApi.readAccessToken());

    const updateConversationHandler = (data) => {
      console.log({ conversationId, dataConvoId: data.conversation_id });
      if (conversationId && data.conversation_id === conversationId) {
        if (data.summary_md) {
          updateSummary(data.summary_md);
          onSummaryCallback && onSummaryCallback(data.summary_md, true);
        }

        console.log("updateConversationHandler", { data });
        if (hasCall(data)) {
          setShowCallControls(true);
          enableAiInterpretation(data);
        }

        if (data.channel === "voice" && data.status === "closed") {
          setShowCallControls(false);
          hasAudioRef.current = false;
          setHasAudio(false);
          if (interpretationViewRef.current) {
            storeRef.current?.finalizeAll();
          } else if (useStablePipelineRef.current) {
            let promoted = false;
            stableTranscriptsRef.current.forEach((entry, key) => {
              if (entry.status === "recognizing") {
                stableTranscriptsRef.current.set(key, {
                  ...entry,
                  status: "recognized",
                  finalizedDialogue: entry.dialogue,
                  activeWord: ""
                });
                promoted = true;
              }
            });
            if (promoted) {
              const sorted = Array.from(stableTranscriptsRef.current.values())
                .sort(stableTranscriptCompare);
              setTranscriptions(sorted);
            }
          } else {
            let promoted = false;
            allTranscriptsRef.current.forEach((entry, key) => {
              if (entry.status === "recognizing") {
                allTranscriptsRef.current.set(key, {
                  ...entry,
                  status: "recognized",
                  finalizedDialogue: entry.dialogue,
                  activeWord: ""
                });
                promoted = true;
              }
            });
            if (promoted) {
              activeRecognizingRef.current.clear();
              setTranscriptions(Array.from(allTranscriptsRef.current.values()));
            }
          }
        }
      }
    };

    const createConversationHandler = (data) => {
      if (conversationId && data.conversation_id === conversationId) {
        if (hasCall(data)) {
          setShowCallControls(true);
        }
        setConversationEntity(data.conversation_id, data);
      }
    };

    const updateConversationRecipientHandler = (data) => {
      if (conversationId && data.conversation_id === conversationId) {
        if (data.role === "terp") {
          if (["connecting", "connected"].includes(data.connection_state)) {
            setTerpStatus(data.connection_state);
            enableAiInterpretation();
          } else {
            setTerpConnecting(false);
            setTerpStatus("");
          }
        }
      }
    };

    (async () => {
      if (!conversationId) {
        return;
      }

      let conversationResponse: any = currentConversation?.entity || {};

      try {
        const fetched = await ConveyApi.getConversation({ id: conversationId, agencyId });
        if (fetched) conversationResponse = fetched;
      } catch(err) {
        console.log('Error fetching conversation:', err);
      }

      if (conversationResponse?.channel && conversationResponse.channel !== "voice") {
        onUIReadyCallback && onUIReadyCallback();
        return;
      }

      if ($summaryContainer) {
        const summaryNode = document.getElementById($summaryContainer);
        if (summaryNode) summaryRef.current = createRoot(summaryNode);
      }
      socketInstance.listen(ConveySocket.CREATE_CONVERSATION, createConversationHandler, true);

      setTimeout(() => {
        if (conversationResponse?.summary_md) {
          updateSummary(conversationResponse?.summary_md);
          onSummaryCallback && onSummaryCallback(conversationResponse?.summary_md, false);
        }
      }, 100);

      socketInstance.listen(ConveySocket.UPDATE_CONVERSATION, updateConversationHandler, true);
      socketInstance.listen(ConveySocket.UPDATE_CONVERSATION_RECIPIENT, updateConversationRecipientHandler, true);
      socketInstance.listen(ConveySocket.CLOSE_CONVERSATION, updateConversationHandler, true);
    })();

    return () => {
      socketInstance.listenOff(ConveySocket.CREATE_CONVERSATION, createConversationHandler);
      socketInstance.listenOff(ConveySocket.UPDATE_CONVERSATION, updateConversationHandler);
      socketInstance.listenOff(ConveySocket.UPDATE_CONVERSATION_RECIPIENT, updateConversationRecipientHandler);
      socketInstance.listenOff(ConveySocket.CLOSE_CONVERSATION, updateConversationHandler);

      if (summaryRef.current) {
        setTimeout(() => {
          summaryRef.current.unmount();
        });
      }
    };
  }, [conversationId]);

  const mergeBuffer = (a, b) => {
    const c = new a.constructor(a.length + b.length);
    c.set(a);
    c.set(b, a.length);

    return c;
  };

  const int16ToFloat32 = (byteArray) => {
    const intArray = new Int16Array(byteArray);
    const float32Array = new Float32Array(intArray.length);

    for (let i = 0; i < intArray.length; i++) {
      float32Array[i] = intArray[i] / 32768.0;
    }

    return float32Array;
  };

  const stereoToMono = (buffer) => {
    const stereoBuffer = new Int16Array(buffer);
    const monoBuffer = new Int16Array(stereoBuffer.length / 2);

    for (let i = 0, j = 0; i < stereoBuffer.length; i += 2, j++) {
      monoBuffer[j] = (stereoBuffer[i] + stereoBuffer[i + 1]) / 2;
    }

    return monoBuffer;
  };

  const convertBlock = (byteArray) => {
    // Create a new Float32Array to hold the float data
    const intArray = new Uint8Array(byteArray);
    const float32Array = new Float32Array(intArray.byteLength / 2);
    // Iterate through the byte array two bytes at a time (for 16-bit samples)
    for (let i = 0; i < intArray.byteLength; i += 2) {
      // Combine the two bytes to form a 16-bit signed integer
      const int16 = (intArray[i + 1] << 8) | (intArray[i] & 0xff);
      // If the integer is negative, handle the sign correctly
      const signedInt16 = int16 >= 0x8000 ? int16 - 0x10000 : int16;
      // Normalize the 16-bit integer to the range [-1.0, 1.0] and store it as a float
      float32Array[i / 2] = signedInt16 / 32768.0;
    }
    return float32Array;
  };

  const openWs = (role) => {
    const wsUrl = `${
      process.env.WS_RTP_HOST
    }?conversationId=${conversationId}&phoneNumber=${correlationId}&primaryRecipient=${correlationId}&agencyId=${
      app.agencyId
    }&source=voice&recipientFlow=true&identity=${
      role === "recipient" ? correlationId : "%2B12222222222"
    }&role=${role}&transcribeProvider=azure&encoding=linear16&langID=true&chunkSize=20&numberChannels=${
      dualChannel ? 2 : 1
    }`;
    // chunkSize=20 <- this is what simulator needs, but daq needs 1000, which is default in RTP index.js
    wsRef.current[role] = new WebSocket(wsUrl);
    wsRef.current[role].binaryType = "arraybuffer";
  };

  const startLangID = async (role: string) => {
    await ConveyApi.updateRecipients({
      number: "12222222222",
      conversationId
    });
    if (!wsRef.current[role]) {
      openWs(role);
    }
    if (wsRef.current[role].readyState === WebSocket.OPEN) {
      initAudio(role);
    } else {
      wsRef.current[role].addEventListener("open", () => {
        initAudio(role);
      });
      wsRef.current[role].addEventListener("close", () => {
        if (!wsConnectInterval.current) {
          wsConnectInterval.current = setInterval(() => {
            if (wsRef.current[role].readyState !== WebSocket.OPEN) {
              openWs(role);
            }
          }, 1000);
        }
      });
      wsRef.current[role].addEventListener("error", () => {
        if (!wsConnectInterval.current) {
          wsConnectInterval.current = setInterval(() => {
            if (wsRef.current[role].readyState !== WebSocket.OPEN) {
              openWs(role);
            }
          }, 1000);
        }
      });
    }
  };

  const startDualLangID = () => {
    startLangID("recipient");
    if (agentAudioFile) {
      startLangID("agent");
    }
  };

  useEffect(() => {
    if (agentAudioFile || recipientAudioFile) {
      setTimeout(() => {
        startDualLangID();
      }, 750);
    }
  }, [agentAudioFile, recipientAudioFile]);

  const initAudio = async (role) => {
    let file = null;
    if (agentAudioFile && role === "agent") {
      file = agentAudioFile;
    } else if (recipientAudioFile && role === "recipient") {
      file = recipientAudioFile;
    } else {
      const files = (document.getElementById(`${role}File`) as HTMLInputElement).files;
      file = files[0];
    }
    if (!file) {
      return;
    }
    const chunksize = dualChannel ? 640 : 320;
    let offset = 0;
    while (offset < file.size) {
      const chunkfile = await file.slice(offset, offset + chunksize);
      // Blob.arrayBuffer() can be polyfilled with a FileReader
      const chunk = await chunkfile.arrayBuffer();
      if (wsRef.current[role].readyState === WebSocket.OPEN) {
        wsRef.current[role].send(chunk);
      }
      await delay(20);
      offset += chunksize;
    }
    wsRef.current[role].close();
  };

  const updateSummary = (summary: string) => {
    if (!summaryRef.current) {
      return;
    }

    summaryRef.current.render(<Markdown>{summary}</Markdown>);
  };

  useEffect(() => {
    getAutoSpeakStatus();
    enableAiInterpretation();

    const sipAgencyId = currentConversation?.entity?.agency_id || agencyId;
    const sipSocketInstance = ConveySocket.initSip({ agencyId: sipAgencyId, correlationId });
    liveAudioTargetRef.current = { agencyId: sipAgencyId, correlationId };

    const sipAudioHandler = async (sipData) => {
      const { msg: data, channelsCount: numberChannels } = sipData;
      dualChannelRef.current = numberChannels === 2;
      hasAudioRef.current = true;
      setHasAudio(true);
      audioArray.current = mergeBuffer(
        audioArray.current ? audioArray.current : new Float32Array(0),
        dualChannelRef.current ? int16ToFloat32(stereoToMono(data)) : convertBlock(data)
      );
      const minChannelBytes = dualChannelRef.current ? 320 : 640;
      if (audioArray.current && audioArray.current.byteLength >= minChannelBytes) {
        onLiveAudioCallback && onLiveAudioCallback(audioArray.current, dualChannelRef.current, true);

        if (audioNodeRef.current) {
          audioNodeRef.current.port.postMessage({ message: "audioData", audioData: audioArray.current });
        }
        audioArray.current = new Float32Array(0);
      }
    };

    const sipSentimentHandler = (data) => {
      if (data.hexColor) {
        setBackgroundColor(`#${data.hexColor}`);
      }
      if (data.providerData?.data?.score) {
        setSentimentScore(data.providerData?.data?.score);
        const createdAt = moment.utc().valueOf();
        if (sentimentScoresRef.current.length > 0) {
          const firstScore = sentimentScoresRef.current[0];
          sentimentScoresRef.current.push({
            score: data.providerData?.data?.score,
            timestamp: (createdAt - firstScore.createdAt) / 1000,
            createdAt,
            channel: data.providerData?.data?.channel
          });
        } else {
          sentimentScoresRef.current.push({
            score: data.providerData?.data?.score,
            timestamp: 0,
            createdAt,
            channel: data.providerData?.data?.channel
          });
        }
        onSentimentScoreCallback && onSentimentScoreCallback(data, true);
      }
    };

    const sipRoomHandler = (data) => {
      // TEMP raw capture (legacy recognizing/recognized/utteranceEnd path) for
      // backend speaker-attribution diagnosis. See CONVEY_RAW_STABLE note above.
      if (isStreamDebug() && data) {
        // eslint-disable-next-line no-console
        console.log("CONVEY_RAW_ROOM", JSON.stringify({
          eventType: data.eventType, id: data.id, status: data.status,
          direction: data.direction, channel: data.channel, identity: data.identity,
          speaker: data.speaker, transcription: data.transcription, language: data.language,
          languages: data.languages, seq: data.seq, createdAt: data.createdAt, start: data.start
        }));
      }
      // Interpretation view is fully driven by the sip:stable + interpretation
      // channels via the deterministic store; the legacy raw sip: channel is
      // ignored here (it duplicates the stable channel).
      if (interpretationViewRef.current) {
        callback && callback(data);
        return;
      }

      if (useStablePipelineRef.current && data.eventType !== "utteranceEnd") {
        callback && callback(data);
        return;
      }

      const { eventType } = data;

      if (eventType === "utteranceEnd") {
        if (typeof data.lastWordEnd === "number") {
          utteranceEndsRef.current.push(data.lastWordEnd);
        }
        if (!useStablePipelineRef.current) {
          const direction = data.channel === 0 ? "inbound" : "outbound";
          activeRecognizingRef.current.forEach((segKey, speakerIdentity) => {
            const entry = allTranscriptsRef.current.get(segKey);
            if (!entry || entry.direction !== direction) return;
            if (entry.status === "recognizing") {
              allTranscriptsRef.current.set(segKey, {
                ...entry,
                status: "recognized",
                finalizedDialogue: entry.dialogue,
                activeWord: ""
              });
              recentUtteranceFinalRef.current.set(entry.uid, segKey);
            }
            activeRecognizingRef.current.delete(speakerIdentity);
          });
          legacyUtteranceGenRef.current.set(
            direction,
            (legacyUtteranceGenRef.current.get(direction) ?? 0) + 1
          );
          setTranscriptions(Array.from(allTranscriptsRef.current.values()));
        }
        setTimeout(() => {
          scrollToBottom();
        }, 250);
        return;
      }

      const {
        direction,
        status,
        transcription,
        translations,
        identity,
        languages,
        id,
        speaker,
        conversationId: transcriptConversationId,
        correlationId: transcriptCorrelationId,
        createdAt,
        providerData,
        confidence,
        start,
        duration,
        seq
      } = data;

      if (conversationId && transcriptConversationId && transcriptConversationId !== conversationId) {
        return;
      }
      if (correlationId && transcriptCorrelationId && transcriptCorrelationId !== correlationId) {
        return;
      }

      const isAgent = direction === "outbound";
      const isCaller = direction === "inbound";

      callback && callback(data);

      let highlightWords = [];
      if (providerData && ["deepgram", "azure", "aws"].includes(providerData.provider)) {
        highlightWords = findKeywords(providerData.data);
      }

      let dialogue;
      let awaitingTranslation = false;

      let englishText = "";
      if (languages && languages.to && languages.to.substring(0, 2) === "en" && translations && translations.to) {
        englishText = translations.to;
      }
      if (languages && isCaller && languages.from !== recipientLanguageRef.current) {
        recipientLanguageRef.current = languages.from;
        setRecipientLanguage(languages.from);
      }

      const sourceLang = (languages && languages.from) || data.language || "";
      const sourceIsForeign = !!sourceLang && !sourceLang.startsWith("en");

      if (englishText) {
        dialogue = englishText;
      } else if (sourceIsForeign) {
        awaitingTranslation = true;
        dialogue = transcription;
      } else if (transcription) {
        // English speech — show as-is.
        dialogue = transcription;
      }
      const originalBody = dialogue;

      // Use the backend-resolved identity directly; fall back to
      // direction-based for backward compat.
      let formattedIdentity: string;
      if (identity) {
        const decoded = decodeURIComponent(identity);
        // Explicit party identity wins over direction (outbound≠always Agent).
        if (decoded === "Caller" || decoded === "Interpreter" || decoded.startsWith("AI Interpreter")) {
          formattedIdentity = decoded;
        } else if (decoded === "Agent" || direction === "outbound") {
          formattedIdentity = "Agent";
        } else {
          formattedIdentity = formatPhoneNumber(decoded);
        }
      } else {
        formattedIdentity = isAgent ? "Agent" : "Caller";
      }

      if (!dialogue || dialogue == "" || (status == "recognizing" && confidence < 0.5)) {
        return;
      }

      if (status === 'recognized' && isAgent) {
        setLastRecognizedAgentTranscript(transcription);
      }

      if (highlightWords && highlightWords.length > 0) {
        highlightWords.forEach((words) => {
          const { direction: keywordDirection, level } = allKeywordsRef.current[words[0]];
          const highlightKeyword =
            keywordDirection === null ||
            (isAgent && keywordDirection?.includes("agent")) ||
            (!isAgent && keywordDirection?.includes("caller"));
          if (
            highlightKeyword &&
            words[1] &&
            dialogue.toLowerCase().includes(words[1]) &&
            Object.keys(allKeywordsRef.current).includes(words[1])
          ) {
            let highlightColor = "#ef5350";
            if (level === 1) {
              highlightColor = "#ef5350";
            } else if (level === 2) {
              highlightColor = "#ffb22b";
            } else if (level === 3) {
              highlightColor = "#186dde";
            }
            const regEx = new RegExp(words[1], "gi");
            dialogue = formatSentence(
              dialogue.replace(regEx, `<strong style="color: ${highlightColor}">` + words[1] + "</strong>")
            );
            const keywordPayload = {
              term: words[0],
              data: allKeywordsRef.current[words[0]],
              id,
              created_at: createdAt,
              body: originalBody
            };

            const exists = foundKeywordsRef.current.findIndex((t) => t.id === id && t.term === words[0]);
            if (exists === -1) {
              foundKeywordsRef.current.push(keywordPayload);
              onKeywordCallback && onKeywordCallback(keywordPayload, true);
            }
          }
        });
      }
      setFoundKeywords(foundKeywordsRef.current);

      if (!transcriptsIdentitiesRef.current.includes(formattedIdentity)) {
        transcriptsIdentitiesRef.current.push(formattedIdentity);
      }

      let finalizedDialogue = dialogue;
      let activeWord = "";
      if (status === "recognizing" && dialogue) {
        const trimmed = dialogue.trimEnd();
        const lastSpace = trimmed.lastIndexOf(" ");
        if (lastSpace > 0) {
          finalizedDialogue = trimmed.substring(0, lastSpace);
          activeWord = trimmed.substring(lastSpace + 1);
        } else {
          finalizedDialogue = "";
          activeWord = trimmed;
        }
      }

      // Determine the map key to use. We want POSITION STABILITY: once an
      // entry is inserted, it must never move. Map.set with an existing key
      // updates the value in place (preserves iteration position).
      //
      // - recognizing same uid → update in place (text grows, position stable)
      // - recognizing new uid (new utterance, same speaker) → use NEW key at
      //   end, but first update the OLD recognizing entry to recognized status
      //   (promotes it in place so it stops underline, preserves position)
      // - recognized with same uid as active recognizing → update in place
      // - recognized with DIFFERENT uid from active recognizing → update the
      //   OLD entry in place with the new text (Deepgram refined start time,
      //   uid changed but it's the same utterance)

      const gen = legacyUtteranceGenRef.current.get(direction) ?? 0;
      const segId = gen > 0 ? `${id}#g${gen}` : id;
      let mapKey = segId;

      if (status === "recognizing") {
        const prevRecognizingKey = activeRecognizingRef.current.get(formattedIdentity);
        if (prevRecognizingKey && prevRecognizingKey !== segId) {
          // New utterance for this speaker. Promote old entry to recognized
          // in place (stops underline, preserves position).
          const oldEntry = allTranscriptsRef.current.get(prevRecognizingKey);
          if (oldEntry) {
            allTranscriptsRef.current.set(prevRecognizingKey, {
              ...oldEntry,
              status: "recognized",
              finalizedDialogue: oldEntry.dialogue,
              activeWord: ""
            });
          }
        }
        activeRecognizingRef.current.set(formattedIdentity, segId);
      }

      if (status === "recognized") {
        const prevRecognizingKey = activeRecognizingRef.current.get(formattedIdentity);
        if (prevRecognizingKey && prevRecognizingKey !== segId) {
          // Deepgram refined the start time so uid changed, but it's the same
          // utterance. Reuse the old key to update in place (position stable).
          if (allTranscriptsRef.current.has(prevRecognizingKey)) {
            mapKey = prevRecognizingKey;
          }
        } else if (!prevRecognizingKey) {
          const finalizedKey = recentUtteranceFinalRef.current.get(id);
          if (finalizedKey && allTranscriptsRef.current.has(finalizedKey)) {
            mapKey = finalizedKey;
          }
        }
        activeRecognizingRef.current.delete(formattedIdentity);
        recentUtteranceFinalRef.current.delete(id);
      }

      allTranscriptsRef.current.set(mapKey, {
        line: `${formattedIdentity}: ${dialogue}`,
        dialogue,
        finalizedDialogue,
        activeWord,
        identity: formattedIdentity,
        transcription,
        spokenText: transcription,
        status,
        direction,
        sourceLang,
        language: sourceLang,
        id: mapKey,
        uid: id,
        created_at: createdAt,
        start,
        seq: seq || 0,
        duration,
        data,
        speaker,
        awaitingTranslation
      });

      setTranscriptions(Array.from(allTranscriptsRef.current.values()));
      setTimeout(() => {
        scrollToBottom();
      }, 250);
    };

    const sipStableHandler = (data) => {
      // TEMP raw capture for backend speaker-attribution diagnosis. Enable with
      // window.__CONVEY_STREAM_DEBUG = true, capture two runs (one correct, one
      // with the caller/agent swap), and share the CONVEY_RAW_STABLE lines.
      if (isStreamDebug() && data) {
        // eslint-disable-next-line no-console
        console.log("CONVEY_RAW_STABLE", JSON.stringify({
          id: data.id, role: data.role, direction: data.direction, channel: data.channel,
          isFinal: data.isFinal, revision: data.revision, displaySeq: data.displaySeq,
          text: data.text, language: data.language, languages: data.languages,
          source: data.source, createdAt: data.createdAt, start: data.start
        }));
      }
      if (interpretationViewRef.current) {
        const eventConversationId = data?.conversationId || data?.conversation_id || data?.callId;
        if (conversationId && eventConversationId && eventConversationId !== conversationId) {
          return;
        }
        storeRef.current?.ingestStable(data);
        callback && callback(data);
        return;
      }

      if (!data || data.status === "suppressed") return;

      useStablePipelineRef.current = true;

      let { id, role, text, isFinal, revision, direction, start } = data;

      // Terp live-preview lines stream under id "live:<role>". Everything else
      // is an assembler segment (direct calls) or a buffered committed line (terp).
      const isLivePreview = typeof id === "string" && id.startsWith("live:");

      if (data.source === "type-to-talk") {
        role = "agent";
        direction = "outbound";
      }

      // Translation update for an already-rendered (or hidden) segment
      if (data.isTranslationUpdate) {
        const existing = stableTranscriptsRef.current.get(id);
        if (existing && data.translations) {
          const translatedText = data.translations.to || existing.dialogue;
          stableTranscriptsRef.current.set(id, {
            ...existing,
            dialogue: translatedText,
            finalizedDialogue: translatedText,
            transcription: translatedText,
            line: `${existing.identity}: ${translatedText}`,
            status: "recognized",
            awaitingTranslation: false,
            revision,
            hidden: false,
            sourceLang: (data.languages && data.languages.from) || existing.sourceLang,
            data: { ...existing.data, translations: data.translations, languages: data.languages }
          });
          const sorted = Array.from(stableTranscriptsRef.current.values())
            .filter((t) => interpretationViewRef.current || !t.hidden)
            .sort(stableTranscriptCompare);
          setTranscriptions(sorted);
        }
        return;
      }

      // A committed line for THIS role will replace that role's live preview.
      // Deletion happens only after revision validation below — otherwise a
      // stale final would wipe the recognizing card and then no-op (disappear).
      // IMPORTANT: never delete the other party's live:* keys here.

      // Render only committed lines and terp live previews. Raw assembler
      // interims (direct 2-channel calls) are NOT rendered: both channels
      // transcribe at once, so their per-word interims interleave and visually
      // mix the two speakers. Committed lines appear cleanly, one per turn.
      if (!isFinal && !isLivePreview) return;

      const historicalCount = allTranscriptsRef.current.size;
      const displaySeq = (data.displaySeq || 0) + historicalCount;

      let dialogue = text;
      let awaitingTranslation = false;
      const sourceLang =
        (data.languages && data.languages.from) ||
        (data.translations && data.translations.language) ||
        data.language ||
        "";
      const sourceIsForeign = !!sourceLang && !String(sourceLang).toLowerCase().startsWith("en");
      if (
        data.translations &&
        data.languages &&
        data.languages.to &&
        data.languages.to.substring(0, 2) === "en" &&
        data.translations.to
      ) {
        dialogue = data.translations.to;
      } else if (sourceIsForeign) {
        awaitingTranslation = true;
      }


      let finalizedDialogue = dialogue;
      let activeWord = "";

      if (!isFinal && dialogue) {
        const trimmed = dialogue.trimEnd();
        const lastSpace = trimmed.lastIndexOf(" ");
        if (lastSpace > 0) {
          finalizedDialogue = trimmed.substring(0, lastSpace);
          activeWord = trimmed.substring(lastSpace + 1);
        } else {
          finalizedDialogue = "";
          activeWord = trimmed;
        }
      }

      const status = isFinal ? "recognized" : "recognizing";

      const existing = stableTranscriptsRef.current.get(id);
      // Speaker lock: an utterance id's speaker/direction is immutable once first
      // seen. A genuine speaker change is keyed by the backend under a NEW id
      // (role split), so a differing role on the SAME id is a spurious
      // cross-channel flip — keep the first attribution so identical events always
      // render the same speaker (no caller<->agent jitter within a run).
      const lockedRole = existing && existing.speaker ? existing.speaker : role;
      const lockedDirection = existing && existing.direction ? existing.direction : direction;
      const formattedIdentity = capitalize(lockedRole);
      // Original dedup: reject any event (interim OR final) that doesn't advance
      // the revision. This is what stops a stale/out-of-order assembler interim
      // (common on 2-channel direct calls with bleed + utterance splits) from
      // reverting an already-finalized line and causing the show/disappear
      // flicker. The terp live-preview lines (id "live:<role>") intentionally
      // reuse a fixed revision and must always update in place, so they bypass
      // this check.
      if (!isLivePreview && existing && existing.revision >= revision) return;

      // Prefix-extension / near-duplicate de-dup (agent finals often restate with
      // mid-word STT drift: "…the ACT?" then "…the accident?").
      // IMPORTANT: do not delete live:<role> until this block finishes — early
      // returns here used to wipe the speaking card and then no-op (vanish).
      if (isFinal && !isLivePreview && !existing) {
        const newText = (text || "").trim();
        const norm = (s: string) =>
          s
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, " ")
            .replace(/\s+/g, " ")
            .trim();
        const newNorm = norm(newText);
        let prevKey: string | null = null;
        let prevSeq = -Infinity;
        stableTranscriptsRef.current.forEach((t, k) => {
          if (k === id || t.hidden || t.speaker !== role) return;
          if (typeof t.displaySeq === "number" && t.displaySeq > prevSeq) {
            prevSeq = t.displaySeq;
            prevKey = k;
          }
        });
        if (prevKey) {
          const prev = stableTranscriptsRef.current.get(prevKey);
          const prevText = (prev?.transcription || "").trim();
          const prevNorm = norm(prevText);
          if (prevText && newText.length > prevText.length && newText.startsWith(prevText)) {
            stableTranscriptsRef.current.delete(prevKey);
          } else if (prevNorm && newNorm && (newNorm === prevNorm || newNorm.startsWith(prevNorm) || prevNorm.startsWith(newNorm))) {
            // Same utterance restated — drop the shorter line.
            if (newText.length >= prevText.length) {
              stableTranscriptsRef.current.delete(prevKey);
            } else {
              return; // keep prev, ignore this shorter final
            }
          } else if (prevNorm && newNorm) {
            const minLen = Math.min(prevNorm.length, newNorm.length);
            if (minLen >= 12) {
              let i = 0;
              while (i < minLen && prevNorm[i] === newNorm[i]) i += 1;
              if (i / minLen >= 0.65) {
                if (newText.length >= prevText.length) {
                  stableTranscriptsRef.current.delete(prevKey);
                } else {
                  return;
                }
              }
            }
          }
        }
      }

      // Clear live preview only once the committed line will actually be written.
      if (isFinal && role) {
        stableTranscriptsRef.current.delete(`live:${role}`);
      }

      stableTranscriptsRef.current.set(id, {
        line: `${formattedIdentity}: ${dialogue}`,
        dialogue,
        finalizedDialogue,
        activeWord,
        identity: formattedIdentity,
        transcription: text,
        spokenText: data.translations && data.translations.from ? data.translations.from : text,
        status,
        direction: lockedDirection,
        sourceLang,
        language: sourceLang,
        id,
        displaySeq,
        created_at: data.createdAt,
        start,
        duration: data.duration,
        revision,
        data,
        speaker: lockedRole,
        awaitingTranslation,
        // Show nothing until the English translation arrives: a foreign turn
        // stays hidden (filtered from the sorted list) until updateTranslation
        // fills it in, at which point hidden is cleared. Replaces the old
        // "Listening…" placeholder.
        hidden: awaitingTranslation
      });

      if (!transcriptsIdentitiesRef.current.includes(formattedIdentity)) {
        transcriptsIdentitiesRef.current.push(formattedIdentity);
      }

      const sorted = Array.from(stableTranscriptsRef.current.values())
        .filter((t) => interpretationViewRef.current || !t.hidden)
        .sort(stableTranscriptCompare);
      setTranscriptions(sorted);

      setTimeout(() => {
        scrollToBottom();
      }, 250);
    };

    // Interpretation view: Type-to-Talk lines are pushed straight into the
    // deterministic store as an agent utterance. The store + CharStream own all
    // pacing/reveal, so there is no component-level typewriter queue anymore.
    const injectTypeToTalk = (englishText: string, foreignText?: string) => {
      const english = (englishText || "").trim();
      if (!english || !interpretationViewRef.current) return;
      storeRef.current?.ingestTypeToTalk(english, foreignText);
      scrollToBottom();
    };

    const typeToTalkWindowHandler = (evt: Event) => {
      const detail = (evt as CustomEvent)?.detail || {};
      if (conversationId && detail.conversationId && detail.conversationId !== conversationId) {
        return;
      }
      if (detail.englishText) {
        injectTypeToTalk(detail.englishText, detail.foreignText);
      }
    };
    window.addEventListener("convey:type-to-talk", typeToTalkWindowHandler as EventListener);

    // Interpretation deltas flow straight into the store, which binds them to the
    // correct utterance (by turnId) and appends idempotently — no per-component
    // chunk maps, sequence tracking or reveal timers.
    const interpretationHandler = (data) => {
      if (!interpretationViewRef.current || !data) return;
      if (conversationId && data.callId && data.callId !== conversationId) return;
      storeRef.current?.ingestInterpretation(data);
    };

    sipSocketInstance
      .listen(ConveySocket.getSipAudio({ agencyId: sipAgencyId, correlationId }), sipAudioHandler)
      .listen(ConveySocket.getSipSentiment({ agencyId: sipAgencyId, correlationId }), sipSentimentHandler)
      .listen(ConveySocket.getSipRoom({ agencyId: sipAgencyId, correlationId }), sipRoomHandler, true)
      .listen(ConveySocket.getSipStable({ agencyId: sipAgencyId, correlationId }), sipStableHandler, true)
      .listen(ConveySocket.INTERPRETATION, interpretationHandler, true);

    return () => {
      window.removeEventListener("convey:type-to-talk", typeToTalkWindowHandler as EventListener);
      if (ConveySocket.isSipAudioJoined({ agencyId: sipAgencyId, correlationId })) {
        ConveySocket.leaveSipAudio({ agencyId: sipAgencyId, correlationId });
      }
      sipSocketInstance
        .listenOff(ConveySocket.getSipAudio({ agencyId: sipAgencyId, correlationId }), sipAudioHandler)
        .listenOff(ConveySocket.getSipSentiment({ agencyId: sipAgencyId, correlationId }), sipSentimentHandler)
        .listenOff(ConveySocket.getSipRoom({ agencyId: sipAgencyId, correlationId }), sipRoomHandler)
        .listenOff(ConveySocket.getSipStable({ agencyId: sipAgencyId, correlationId }), sipStableHandler)
        .listenOff(ConveySocket.INTERPRETATION, interpretationHandler);
    };
  }, []);

  const playPCM = async () => {
    if (!audioContextRef.current || !pcmDataUrl) return;

    try {
      const response = await fetch(pcmDataUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioData = int16ToFloat32(stereoToMono(arrayBuffer));

      const numChannels = currentConversation?.entity?.metadata?.channelsCount;

      const sampleRate = 8_000;
      const pcmLength = audioData.byteLength / 2; // Assuming 16-bit PCM

      const audioBuffer = audioContextRef.current.createBuffer(numChannels, pcmLength, sampleRate);

      const data = new Float32Array(audioData);
      audioBuffer.copyToChannel(data, 0);

      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      source.start();
      onLiveAudioCallback && onLiveAudioCallback(audioData, numChannels == 2, false);
      setPcmLoaded(true);
    } catch (error) {
      console.error("Error playing PCM:", error);
    }
  };

  // Live audio is pull based: pressing "Live" joins the call's dedicated SocketIO
  // room and pressing it again leaves it, so stopping playback also stops the
  // stream crossing the wire instead of only muting the speakers. The recorded
  // media player below owns its own Play/Stop and never touches the room.
  const toggleLiveAudio = () => {
    const target = liveAudioTargetRef.current;
    if (!audioContextRef.current || !target) return;

    if (isPlaying) {
      audioContextRef.current.suspend();
      ConveySocket.leaveSipAudio(target);
      setIsPlaying(false);
      announcePolite("Live audio stopped");
      return;
    }

    ConveySocket.joinSipAudio(target);
    audioContextRef.current.resume();
    setIsPlaying(true);
    announcePolite("Live audio started");
  };

  // A call that ends — or a recording that lands while somebody is listening —
  // has to release the subscription as well, otherwise the browser stays in a
  // room nobody publishes to.
  useEffect(() => {
    if (liveAudioAvailable) return;

    const target = liveAudioTargetRef.current;
    if (!target || !ConveySocket.isSipAudioJoined(target)) return;

    audioContextRef.current?.suspend();
    ConveySocket.leaveSipAudio(target);
    setIsPlaying(false);
  }, [liveAudioAvailable]);

  const initAudioContext = async () => {
    if (audioNodeRef.current) {
      return;
    }
    try {
      const audioContext = new AudioContext({ sampleRate: 8000 });
      // Registering the worklet has to succeed before the node is built — the
      // rejection used to be swallowed here, which turned a module failure into
      // a silent player that looked ready.
      const audioNode = await createAudioProcessorNode(audioContext);
      audioNode.connect(audioContext.destination);
      audioNodeRef.current = audioNode;
      setAudioNodeObj(audioNode);
      audioContextRef.current = audioContext;
      audioContextRef.current.suspend();
      setShowAudioPlay(true);
    } catch (e) {
      console.error("[Transcribe:initAudioContext] live audio unavailable: ", e);
    }
  };

  const openUploader = (id: string) => {
    document.getElementById(id).click();
  };

  const handleConnectInterpreter = async () => {
    if (terpStatus === "connecting" || terpStatus === "connected") {
      console.log("CONNECT");
      setTerpConnecting(false);
      await ConveyApi.removeInterpreter({ conversationId, number: "+12222222840" });
    } else {
      console.log("DISCONNET");
      setTerpConnecting(true);
      await ConveyApi.addInterpreter({ conversationId, number: "+12222222840" });
    }
  };

  return (
    <div onClick={initAudioContext} style={{ height: "100%" }}>
      <ActionsContainer style={{ height: 60, padding: "0 10px" }}>
        <FlexCenterRow justify="space-between" style={{ width: "100%" }}>
          <FlexCenterRow justify="start">
            {numberCapabilities &&
              numberCapabilities.includes("terp") &&
              showCallControls &&
              app.initOptions.options.enableInterpreter && (
                <SDKButton
                  data-type="ConnectInterpreter"
                  buttonType="success"
                  onClick={handleConnectInterpreter}
                  buttonOpacity={!terpConnecting ? 1 : 0.5}
                  disabled={terpConnecting}
                >
                  <Phone /> {terpStatus ? capitalize(terpStatus) : "Terp"}
                </SDKButton>
              )}
            {!app.initOptions.options.disableShowLiveAudio && showAudioPlay && liveAudioAvailable && (
              <AudioWrap
                type="button"
                listening={isPlaying}
                aria-pressed={isPlaying}
                aria-label={isPlaying ? "Stop live audio" : "Listen to live audio"}
                onClick={(e) => {
                  e.preventDefault();
                  toggleLiveAudio();
                }}
              >
                {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
                LIVE
              </AudioWrap>
            )}
            {pcmDataUrl && (
              <>
                <AudioWrap
                  type="button"
                  aria-label={isPlaying ? "Pause audio playback" : "Play audio recording"}
                  onClick={(e) => {
                    e.preventDefault();
                    if (audioContextRef.current) {
                      if (isPlaying) {
                        audioContextRef.current.suspend();
                      } else {
                        if (pcmLoaded) {
                          audioContextRef.current.resume();
                        } else {
                          playPCM();
                          audioContextRef.current.resume();
                        }
                      }
                      setIsPlaying(!isPlaying);
                    }
                  }}
                >
                  {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
                  PLAY
                </AudioWrap>
              </>
            )}
            <SwitchWrap style={{ paddingTop: 10, margin: "0 20px", gap: 10 }}>
              <SwitchItem style={{ width: "fit-content" }}>
                <Switch
                  onChange={handleAutoScrollToggle}
                  checked={autoScroll}
                  uncheckedIcon={false}
                  checkedIcon={false}
                />
                Autoscroll
              </SwitchItem>
            </SwitchWrap>
          </FlexCenterRow>
          <FlexCenterRow justify="start">
            {showUploader && (
              <>
                <UploadWrap>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openUploader("recipientFile");
                    }}
                  >
                    Select Recipient Audio
                  </a>
                  <input
                    id="recipientFile"
                    hidden={true}
                    type="file"
                    onChange={() => {
                      setCallerSelected(true);
                    }}
                    accept="audio/*"
                  />
                </UploadWrap>
                <UploadWrap>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openUploader("agentFile");
                    }}
                  >
                    Select Agent Audio
                  </a>
                  <input
                    id="agentFile"
                    hidden={true}
                    type="file"
                    onChange={() => {
                      setAgentSelected(true);
                    }}
                    accept="audio/*"
                  />
                </UploadWrap>
                {agentSelected && callerSelected && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      startDualLangID();
                    }}
                  >
                    Send Audio
                  </a>
                )}
              </>
            )}
            <FormInput
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              placeholder="Search"
              value={search}
              style={{
                boxSizing: "border-box",
                padding: 6,
                width: "100%",
                maxWidth: 600,
                minWidth: 100,
                borderRadius: 4,
                border: "1px solid hsl(0, 0%, 80%)",
                minHeight: 38,
                flex: 1
              }}
            />
          </FlexCenterRow>
        </FlexCenterRow>
      </ActionsContainer>
      {interpretationView ? (
        <TranscribeWrap style={{ flexDirection: "column", height: "calc(100% - 84px)", top: 62, padding: 0 }}>
          <InterpretationView store={getStore()} search={search} getTimestamp={getTimestamp} correlationId={correlationId} autoScroll={autoScroll} onAutoScrollChange={setAutoScrollEnabled} />
        </TranscribeWrap>
      ) : (
      <TranscribeWrap style={{ flexDirection: "row", height: "calc(100% - 84px)", top: 62 }}>
        {app.initOptions.options.enableSentimentAnalysis && (
          <SentimentContainer style={{ backgroundColor }}>
            {sentimentScore && <SentimentScore>{sentimentScore}</SentimentScore>}
          </SentimentContainer>
        )}
        <TranscribeWrapContainer>
          <TranscribeContainer
            id={`transcription-container-${correlationId}`}
            onWheel={handleUserScrollIntent}
            onTouchStart={handleUserScrollIntent}
            onPointerDown={handleUserScrollIntent}
            onScroll={handleTranscribeContainerScroll}
          >
            <div
              style={{
                width: "100%",
                marginLeft: "10px"
              }}
            >
              {transcripts
                .map((transcript, index, arr) => {
                  const previousTranscript = index > 0 ? arr[index - 1] : null;

                  const isSameIdentity = previousTranscript
                    ? previousTranscript.identity === transcript.identity
                    : false;

                  const isPreviousTranscriptSentence = (str: string): boolean => {
                    if (!str) return false;
                    return [".", "?", "!"].some((suffix) => str.endsWith(suffix));
                  };

                  // Robust utterance timestamp: prefer created_at, fall back to
                  // the ms-scale displaySeq (assembler start-based) then start, so
                  // the pause gate still works when created_at is missing/equal
                  // rather than silently never (or always) breaking.
                  const tsOf = (t: any): number => {
                    if (!t) return NaN;
                    const c = t.created_at ? new Date(t.created_at).getTime() : NaN;
                    if (Number.isFinite(c)) return c;
                    if (typeof t.displaySeq === "number") return t.displaySeq;
                    if (typeof t.start === "number") return t.start * 1000;
                    return NaN;
                  };
                  const prevMs = tsOf(previousTranscript);
                  const currMs = tsOf(transcript);
                  // A line break is inserted INSIDE the current speaker card ONLY
                  // when the current utterance's speaker matches the previous one
                  // (isSameIdentity). On a speaker change a new speaker header
                  // (card) is rendered below instead, and no break is added. The
                  // separator is derived per boundary at render time, so it is
                  // inherently idempotent — repeated events for one utterance
                  // update in place and can never emit a duplicate break.
                  const pauseGap =
                    Number.isFinite(prevMs) && Number.isFinite(currMs) ? currMs - prevMs : Infinity;
                  const sameSpeakerLineBreak = isSameIdentity &&
                    pauseGap >= 1250 &&
                    isPreviousTranscriptSentence(previousTranscript?.dialogue?.trim());

                  const isRecognizing = transcript.status === "recognizing";
                  const speakerClass = typeof transcript.speaker !== "undefined"
                    ? `transcript-speaker-${transcript.speaker}`
                    : "";
                  const sourceLang = transcriptSourceLang(transcript);
                  const isAgentLine = transcript.identity === "Agent";
                  const agentEnglish = isAgentLine && isEnglishLang(sourceLang);

                  // While a foreign turn is still awaiting its English
                  // translation, render nothing — the segment appears (in the
                  // correct chronological position) once the translation lands.
                  if (transcript.awaitingTranslation) {
                    return null;
                  }

                  return (
                    (search === "" || transcript.dialogue.includes(search)) && (
                      <Fragment key={transcript.id}>
                        {!isSameIdentity && (
                          <TranscribeTextHeader
                            style={{
                              marginTop: 24
                            }}
                            className="transcript-text-header"
                          >
                            <>
                              <TranscribeTextIdentity>{transcript.identity}</TranscribeTextIdentity>
                              <TranscribeTimestamp isAfterIdentity={true}>
                                {getTimestamp(transcript.created_at)}
                              </TranscribeTimestamp>
                            </>
                          </TranscribeTextHeader>
                        )}
                        {sameSpeakerLineBreak && (
                          <TranscribeTimestamp isAfterIdentity={false} className="transcript-text-timestamp">
                            {getTimestamp(transcript.created_at)}
                          </TranscribeTimestamp>
                        )}
                        <TranscribeTextWrap id={`${transcript.id}`} className="transcript-text-wrap">
                            <TranscribeText
                              id={`transcribe-msg-${transcript.id}`}
                              direction={transcript.direction}
                              emphasized={agentEnglish}
                              className={`transcript-item transcript-identity-${
                                transcript.identity !== "Agent"
                                  ? transcriptsIdentitiesRef.current.indexOf(transcript.identity)
                                  : "responder"
                              }${isRecognizing ? " transcript-interim" : ""}${
                                isAgentLine ? " transcript-agent" : ""
                              }${agentEnglish ? " transcript-lang-en" : ""}`}
                            >
                              <CaptionFade key={isRecognizing ? "interim" : "final"}>
                                {isRecognizing ? (
                                  <>
                                    {transcript.finalizedDialogue && (
                                      <span
                                        dangerouslySetInnerHTML={{ __html: transcript.finalizedDialogue + " " }}
                                        className={speakerClass}
                                      />
                                    )}
                                    {transcript.activeWord && (
                                      <span
                                        className={`transcript-active-word${speakerClass ? ` ${speakerClass}` : ""}`}
                                        dangerouslySetInnerHTML={{ __html: transcript.activeWord + " " }}
                                      />
                                    )}
                                  </>
                                ) : (
                                  <span
                                    dangerouslySetInnerHTML={{ __html: transcript.dialogue + " " }}
                                    className={speakerClass}
                                  />
                                )}
                              </CaptionFade>
                            </TranscribeText>
                        </TranscribeTextWrap>
                      </Fragment>
                    )
                  );
                })}
            </div>
          </TranscribeContainer>
          {app?.initOptions?.options?.enableTTS && (
            <ChatInputContainer>
              <ChatInput
                recipient={false}
                onChange={(e) => {
                  setText(e.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.key === "Enter" || e.keyCode === 13) {
                    createAutoSpeak(text);
                  }
                }}
                value={text}
                disabled={sending}
              />
              <ChatButton
                value="Send"
                data-type="TranscribeTTSSend"
                disabled={sending}
                onClick={() => {
                  createAutoSpeak(text);
                }}
              >
                Send
                {sending && <Spinner />}
              </ChatButton>
            </ChatInputContainer>
          )}
        </TranscribeWrapContainer>
        {app.initOptions.options.enableKeywordBubbles && (
          <KeywordsContainer>
            {foundKeywordsRef.current.map((keyword) => {
              return (
                <Keyword
                  key={[keyword.id, keyword.term].join("")}
                  id={[keyword.id, keyword.term].join("")}
                  level={keyword.data ? keyword.data.level : 1}
                  onClick={() => {
                    const chatMsg = document.getElementById(`transcribe-msg-${keyword.id}`);
                    if (chatMsg) {
                      chatMsg.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
                      chatMsg.classList.add("highlight");
                      setTimeout(() => {
                        chatMsg.classList.remove("highlight");
                      }, 1_000);
                    }
                  }}
                >
                  {keyword.term}
                  <KeywordTimestamp>{getTimestamp(keyword.created_at)}</KeywordTimestamp>
                </Keyword>
              );
            })}
          </KeywordsContainer>
        )}
      </TranscribeWrap>
      )}
    </div>
  );
};

const mapStateToProps = ({ app, agency, conversations }: IRootState) => ({
  app,
  agency,
  conversations
});

export const TranscribeChat = connect(mapStateToProps, {
  setConversationEntity
})(XTranscribeChat);
